import { type ClientVD } from 'vd-server';
import { honoFetcher } from './honoFetcher';

/** Aligned with vd-server `POST /queue/jobs/poll` — only the first N IDs are polled. */
const QUEUE_JOB_POLL_MAX_IDS = 50;

/** Hard cap so the UI never waits unbounded if jobs stay active (e.g. stuck worker). */
export const POLL_FOR_JOB_DEFAULT_MAX_MS = 10 * 60 * 1000;

/**
 * Dataset test runs can take much longer than typical background jobs
 * (long videos + multi-pass agentic analysis). Keep in sync with
 * `DATASET_TEST_EXPIRE_IN_SECONDS` in vd-server `runDatasetTest.ts`.
 */
export const DATASET_TEST_POLL_MAX_MS = 45 * 60 * 1000;

const pollForJobTimeoutMessage =
  'Timed out waiting for background work to finish. Try refreshing in a moment.';

export type PollForJobOptions = {
  maxWaitMs?: number;
};

export const pollForJob = async (
  client: ClientVD,
  queueName: string,
  jobIds: (string | null)[],
  options?: PollForJobOptions,
) => {
  const idsToPoll = (jobIds.filter(Boolean) as string[]).slice(0, QUEUE_JOB_POLL_MAX_IDS);
  if (idsToPoll.length === 0) {
    return;
  }
  const maxWaitMs = options?.maxWaitMs ?? POLL_FOR_JOB_DEFAULT_MAX_MS;
  const deadline = Date.now() + maxWaitMs;

  for (;;) {
    const result = await honoFetcher(
      client.queue.jobs.poll.$post({
        json: {
          queueName,
          jobIds: idsToPoll,
        },
      }),
    );
    const error = result.jobs.find((job) => job.error)?.error;
    if (error) {
      throw new Error(error);
    }
    if (result.allTerminal) {
      return;
    }
    if (Date.now() >= deadline) {
      throw new Error(pollForJobTimeoutMessage);
    }
    if (result.timedOut) {
      continue;
    }
    throw new Error('Unexpected job poll response. Please try again.');
  }
};
